import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Crown, Shield, MoreHorizontal, MessageCircle, Ban } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { ChatChannelDetail } from '../../services/chatChannelService';
import { kickMember, createDM } from '../../services/chatChannelService';
import { useAuthStore } from '../../store/authStore';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';

interface Props {
  channel: ChatChannelDetail;
  onClose: () => void;
}

export default function ChatMemberDrawer({ channel, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id) ?? '';
  const myRole = channel.myRole;
  const [search, setSearch] = useState('');
  const [kickConfirm, setKickConfirm] = useState<{ userId: string; name: string } | null>(null);
  const [kicking, setKicking] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [creatingGroupDM, setCreatingGroupDM] = useState(false);

  const filtered = search
    ? channel.members.filter((m) => m.name.toLowerCase().includes(search.toLowerCase()))
    : channel.members;

  const handleKick = async () => {
    if (!kickConfirm) return;
    setKicking(true);
    try {
      await kickMember(channel.id, kickConfirm.userId);
      toast.success(`${kickConfirm.name}님이 채널에서 추방되었습니다.`);
      queryClient.invalidateQueries({ queryKey: ['chatChannel', channel.id] });
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      setKickConfirm(null);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '추방에 실패했습니다.';
      toast.error(msg);
    } finally {
      setKicking(false);
    }
  };

  const handleDM = async (targetUserId: string) => {
    try {
      const result = await createDM([targetUserId]);
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      onClose();
      navigate(`/chat/${result.id}`);
    } catch {
      toast.error('DM 생성에 실패했습니다.');
    }
  };

  const toggleSelect = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else if (next.size < 9) next.add(userId);
      return next;
    });
  };

  const handleGroupDM = async () => {
    if (selectedIds.size === 0) return;
    setCreatingGroupDM(true);
    try {
      const result = await createDM([...selectedIds]);
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      onClose();
      navigate(`/chat/${result.id}`);
    } catch {
      toast.error('DM 생성에 실패했습니다.');
    } finally {
      setCreatingGroupDM(false);
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const canKick = (memberRole: string) => {
    if (channel.type === 'PROJECT') return false;
    if (memberRole === 'OWNER') return false;
    if (myRole === 'OWNER') return true;
    if (myRole === 'ADMIN' && memberRole === 'MEMBER') return true;
    return false;
  };

  return (
    <div className="w-[280px] min-w-[280px] bg-white border-l border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-gray-100 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900">
          {selectMode ? '멤버 선택' : `멤버 (${channel.memberCount})`}
        </h3>
        <div className="flex items-center gap-1">
          {selectMode && (
            <button onClick={exitSelectMode} className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1">취소</button>
          )}
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="size-4" /></button>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="멤버 검색..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 bg-gray-50" />
        </div>
      </div>

      {/* Member list */}
      <div className="flex-1 overflow-y-auto px-2">
        {filtered.map((member) => (
          selectMode ? (
            /* Select mode: checkboxes */
            member.userId !== currentUserId ? (
              <label key={member.userId}
                className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selectedIds.has(member.userId)}
                  onChange={() => toggleSelect(member.userId)}
                  disabled={!selectedIds.has(member.userId) && selectedIds.size >= 9}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <Avatar
                  userId={member.userId}
                  name={member.name}
                  avatarUrl={member.avatarUrl}
                  size="sm"
                />
                <span className="text-sm text-gray-900 truncate">{member.name}</span>
              </label>
            ) : null
          ) : (
            /* Normal mode: with dropdown */
            <MemberRow
              key={member.userId}
              member={member}
              isMe={member.userId === currentUserId}
              showKick={canKick(member.role)}
              showDM={member.userId !== currentUserId}
              onKick={() => setKickConfirm({ userId: member.userId, name: member.name })}
              onDM={() => handleDM(member.userId)}
            />
          )
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">검색 결과가 없습니다.</p>
        )}
      </div>

      {/* Bottom actions */}
      {selectMode ? (
        <div className="border-t border-gray-100 px-3 min-h-[56px] flex items-center shrink-0">
          <div className="w-full space-y-1">
            <p className="text-sm text-gray-500 text-center">{selectedIds.size}/9명 선택</p>
            {selectedIds.size >= 9 && (
              <p className="text-sm text-amber-600 text-center">최대 10명까지 참여할 수 있습니다</p>
            )}
            <button onClick={handleGroupDM} disabled={selectedIds.size === 0 || creatingGroupDM}
              className="w-full px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors">
              {creatingGroupDM ? '생성 중...' : '대화 시작'}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-gray-100 px-3 h-14 flex items-center shrink-0">
          <button onClick={() => setSelectMode(true)}
            className="w-full px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
            그룹 DM 만들기
          </button>
        </div>
      )}

      {/* Kick confirmation dialog */}
      {kickConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setKickConfirm(null)}>
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">멤버 추방</h3>
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">'{kickConfirm.name}'</span>님을 채널에서 추방하시겠습니까?
            </p>
            <p className="text-sm text-gray-500 mb-5">추방된 멤버는 더 이상 이 채널의 메시지를 볼 수 없습니다.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setKickConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">취소</button>
              <button onClick={handleKick} disabled={kicking}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50">
                {kicking ? '추방 중...' : '추방'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberRow({ member, isMe, showKick, showDM, onKick, onDM }: {
  member: { userId: string; name: string; avatarUrl: string | null; role: string };
  isMe: boolean; showKick: boolean; showDM: boolean;
  onKick: () => void; onDM: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { openProfileModal } = useProfileModal();

  useEffect(() => {
    if (!menuOpen) return;
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  const hasActions = !isMe && (showDM || showKick);

  return (
    <div className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-gray-50 group">
      <button
        type="button"
        onClick={() => openProfileModal(member.userId)}
        className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
        aria-label={`${member.name}의 프로필`}
      >
        <Avatar
          userId={member.userId}
          name={member.name}
          avatarUrl={member.avatarUrl}
          size="sm"
        />
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-sm text-gray-900 truncate hover:underline">{member.name}</span>
          {member.role === 'OWNER' && <Crown className="size-3 text-amber-500 shrink-0" />}
          {member.role === 'ADMIN' && <Shield className="size-3 text-blue-500 shrink-0" />}
        </div>
      </button>

      {hasActions && (
        <div ref={menuRef} className="relative">
          <button onClick={() => setMenuOpen((v) => !v)}
            className="p-1 text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity rounded">
            <MoreHorizontal className="size-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30">
              {showDM && (
                <button onClick={() => { setMenuOpen(false); onDM(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <MessageCircle className="size-4" /> 다이렉트 메시지
                </button>
              )}
              {showKick && (
                <>
                  {showDM && <div className="border-t border-gray-100 my-1" />}
                  <button onClick={() => { setMenuOpen(false); onKick(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50">
                    <Ban className="size-4" /> 추방
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
